import * as React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
} from '@mui/material';
import { Cancel, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import Navbar from '../setup/Navbar';
import Loading from '../../BuiltInComponent/Loading';
import '../../services/GlobalVariables';

export default function OrderCancellation() {
  const { user } = useSelector((state) => state.auth);

  const [orders, setOrders] = React.useState([]);
  const [filteredOrders, setFilteredOrders] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [openingDate, setOpeningDate] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const loadOrders = React.useCallback(async () => {
    if (!user?.userID) return;

    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${global.SET_API_URL}posBusiness/getCancelableOrders/${user.userID}`
      );

      if (response.data?.success) {
        const list = response.data.orders || [];
        setOrders(list);
        setFilteredOrders(list);
        setOpeningDate(response.data.openingDate || '');
      } else {
        setError(response.data?.message || 'Unable to fetch orders');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [user?.userID]);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  React.useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setFilteredOrders(orders);
      return;
    }

    const filtered = orders.filter((order) => {
      const orderId = String(order.orderID || '').toLowerCase();
      const table = String(order.tableLabel || '').toLowerCase();
      const customer = String(order.customerName || '').toLowerCase();
      const type = String(order.orderType || '').toLowerCase();
      return orderId.includes(q) || table.includes(q) || customer.includes(q) || type.includes(q);
    });

    setFilteredOrders(filtered);
  }, [search, orders]);

  const handleCancelOrder = async (orderID) => {
    if (!orderID) return;

    const ok = window.confirm(`Are you sure you want to cancel order #${orderID}?`);
    if (!ok) return;

    if (!user?.userID) {
      alert('Logged-in user not found.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${global.SET_API_URL}posBusiness/cancelOrder`, {
        orderID,
        userID: user.userID,
        reason: reason || null,
      });

      if (response.data?.success) {
        alert(response.data.message || 'Order cancelled successfully');
        await loadOrders();
      } else {
        alert(response.data?.message || 'Order cancellation failed');
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Order cancellation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Order Cancellation
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Opening Date: {openingDate || '-'} | User: {user?.userName || user?.dispName || user?.userID || '-'}
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Search (Order ID / Table / Customer / Type)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Cancellation Reason (Optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadOrders} fullWidth>
              Refresh
            </Button>
          </Stack>
        </Paper>

        {loading && (
          <Box sx={{ mb: 2 }}>
            <Loading loadingText="Loading orders" />
          </Box>
        )}

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <TableRow key={order.orderID} hover>
                    <TableCell>{order.orderID}</TableCell>
                    <TableCell>{order.orderType}</TableCell>
                    <TableCell>{order.tableLabel || '-'}</TableCell>
                    <TableCell>{order.customerName || '-'}</TableCell>
                    <TableCell align="right">{Number(order.grandTotal || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={order.orderStatus || '-'}
                        color={String(order.orderStatus || '').toLowerCase() === 'completed' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => handleCancelOrder(order.orderID)}
                      >
                        Cancel Order
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No orders found for cancellation
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  );
}
